/**
 * Debug panel for disabling shape key layers.
 *
 * Only visible when debug mode is active and the user is in pose mode.
 * Each layer can be toggled independently — disabled layers have their
 * morph target influences zeroed every frame (enforced in MannequinRenderer).
 */

import { useStore } from "../app/store.ts";
import {
  SHAPE_KEY_LAYER_LABELS,
  SHAPE_KEY_LAYERS,
  type ShapeKeyLayer,
} from "../mannequin/shapeKeyLayers.ts";
import { DraggablePanel } from "../ui/DraggablePanel.tsx";
import { usePanelSelection } from "../ui/floatingPanelStore.ts";
import {
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  Z_DEBUG,
} from "../ui/tokens.ts";

export function ShapeKeyLayerDebugPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.shapeKeyLayers);
  const poseMode = useStore((s) => s.poseMode);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-shape-key-layers", () =>
    togglePanel("shapeKeyLayers"),
  );
  const disabledLayers = useStore((s) => s.debugDisabledShapeKeyLayers);
  const toggle = useStore((s) => s.toggleDebugShapeKeyLayer);

  if (!debugEnabled || !panelOpen || !poseMode) return null;

  return (
    <DraggablePanel
      id="debug-shape-key-layers"
      defaultPosition={{ bottom: 8, right: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      selectionColor="#ff0"
      {...sel}
      style={{ minWidth: 200 }}
      header={
        <span
          style={{
            color: "#ff0",
            fontWeight: "bold",
            fontFamily: "monospace",
            fontSize: 10,
          }}
        >
          SHAPE KEY LAYERS
        </span>
      }
    >
      <div
        style={{
          background: "rgba(0,0,0,0.85)",
          color: COLOR_TEXT_SECONDARY,
          fontFamily: "monospace",
          fontSize: 10,
          padding: "6px 10px",
          borderRadius: "0 0 6px 6px",
          pointerEvents: "auto",
          lineHeight: 1.8,
        }}
      >
        {SHAPE_KEY_LAYERS.map((layer) => (
          <LayerRow
            key={layer}
            layer={layer}
            disabled={disabledLayers.has(layer)}
            onToggle={toggle}
          />
        ))}
        <div
          style={{
            color: COLOR_TEXT_DIM,
            marginTop: 4,
            borderTop: "1px solid #333",
            paddingTop: 2,
            fontSize: 9,
          }}
        >
          unchecked layers are zeroed each frame
        </div>
      </div>
    </DraggablePanel>
  );
}

function LayerRow({
  layer,
  disabled,
  onToggle,
}: {
  layer: ShapeKeyLayer;
  disabled: boolean;
  onToggle: (layer: ShapeKeyLayer) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
        color: disabled ? COLOR_TEXT_FAINT : "#0f0",
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={!disabled}
        onChange={() => onToggle(layer)}
        style={{ accentColor: "#0f0", cursor: "pointer" }}
        data-no-drag
      />
      {SHAPE_KEY_LAYER_LABELS[layer]}
    </label>
  );
}
